import { useIsVitrine } from '@/lib/use-instance';
import { VERSION_TAG_LABEL } from '@/lib/versioned-labels';
import type { VersionTag } from '@matter_extractor/shared';
import { Badge, type BadgeTone } from '@pms-digital/design-system';

/**
 * Where a pointer sits: « production v3 », « développement v5 » (refonte
 * modulaire §4).
 *
 * ## The colours are a meaning and not a decoration
 *
 * `vert` for production and `bleu-ciel` for development, at their `-tint` /
 * `-deep` pair so the ink clears the contrast floor on the ground the pill
 * paints. The charte holds this app at level 2 - black, white and `bleu-nuit` -
 * and authorises a noyau where it CARRIES something: here it carries which of
 * the two pointers an operator is looking at, on a screen where every card
 * shows both. Two grey pills would be a pair the eye has to read word by word.
 *
 * ## The production pill is ABSENT and never « v- »
 *
 * §4 is explicit: the pill is left out when nothing has ever been passed to
 * production, « pour que l'œil trouve tout de suite ce qui ne l'a jamais été ».
 * That is why the caller renders `null` rather than this component with a
 * placeholder - a « production - » would be a pill the eye counts and then has
 * to decode, which is the exact opposite of what the absence buys.
 *
 * `version` is optional because the same pill says two things: WHICH version a
 * pointer is on (on a card, in the editor's head) and THAT a pointer is on this
 * line (in the history, where the line already carries its own number).
 *
 * ## On the vitrine it is the plain « v12 », once
 *
 * The vitrine has ONE state (docs/specs/vitrine.md section 7, issue #192), so
 * there is no pointer to tell apart: the production pill says the version the
 * vitrine plays as plain text, and the development pill - which would say the
 * same number again - is not drawn. A pill with no number, which only said THAT
 * a pointer sits on a history line, is not drawn either. Every consumer of this
 * component (the pickers, the chooser, the popovers, the head of a session)
 * gets it without a line of its own.
 *
 * `entity` is the `data-testid` prefix of whatever object is being shown -
 * `critere` gives `critere-pastille-production`. It is a prop and not a
 * constant because this pill is the one component of the layer that EVERY
 * screen of every versioned object mounts, so a hardcoded mark would be the
 * one place a persona list still said « critere ».
 */

const TAG_TONE: Readonly<Record<VersionTag, BadgeTone>> = {
  production: 'success',
  development: 'info',
};

export interface VersionPillProps {
  /** The `data-testid` prefix of the object: « critere » (`VersionedWording.entity`). */
  entity: string;
  tag: VersionTag;
  /** The number to name, or nothing when the surrounding line already names it. */
  version?: number;
  /** Layout only (a margin, `self-start`), worn by the envelope: `Badge` takes no class. */
  className?: string;
}

export function VersionPill({ entity, tag, version, className }: VersionPillProps) {
  const vitrine = useIsVitrine();
  if (vitrine) {
    if (tag === 'development' || version === undefined) return null;
    return (
      <span
        data-testid={`${entity}-version-jouee`}
        className={`font-semibold text-gray-900 text-small leading-5 ${className ?? ''}`}
      >
        v{version}
      </span>
    );
  }
  // The mark rides an envelope: `Badge` takes no `data-testid`. `maxWidth="none"`
  // because « latest prod v12 » is a fact, never a label to cut.
  return (
    <span data-testid={`${entity}-pastille-${tag}`} className={`inline-flex ${className ?? ''}`}>
      <Badge tone={TAG_TONE[tag]} maxWidth="none">
        {version === undefined ? VERSION_TAG_LABEL[tag] : `${VERSION_TAG_LABEL[tag]} v${version}`}
      </Badge>
    </span>
  );
}
